import React, { useEffect, useState } from "react";
import { Settings2, Plus, Trash2, RotateCcw, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const COUNTABLE_UNITS = ["stk", "stück", "anzahl", "ue", "ues"];
const isCountable = (unit) => COUNTABLE_UNITS.includes((unit || "").trim().toLowerCase());

export const KpiEditor = () => {
  const [data, setData] = useState(null);
  const [roleKey, setRoleKey] = useState("");
  const [kpis, setKpis] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/kpi/definitions").then((r) => {
      setData(r.data);
      const rk = roleKey || r.data.roles[0]?.role_key || "";
      setRoleKey(rk);
      setKpis((r.data.roles.find((x) => x.role_key === rk)?.kpis || []).map((k) => ({ ...k })));
    }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selectRole = (rk) => {
    setRoleKey(rk);
    setKpis((data?.roles.find((x) => x.role_key === rk)?.kpis || []).map((k) => ({ ...k })));
  };

  const upd = (i, field, val) => setKpis((arr) => arr.map((k, idx) => {
    if (idx !== i) return k;
    const next = { ...k, [field]: val };
    // Stk/Anzahl/UE-Ziele werden ab jetzt immer automatisch pro Woche runtergerechnet
    // (Monatsziel ÷ 4) — eine eigene "wöchentlich"-Auswahl gibt es dafür nicht mehr.
    if (field === "unit" && isCountable(val)) next.period = "monthly";
    return next;
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/kpi/definitions/${roleKey}`, { kpis: kpis.map((k) => ({ ...k, target: parseFloat(k.target) || 0 })) });
      toast.success("KPIs gespeichert");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    try {
      const { data: d } = await api.delete(`/kpi/definitions/${roleKey}`);
      setKpis((d.kpis || []).map((k) => ({ ...k })));
      toast.success("Auf Standard zurückgesetzt");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (!data) return null;
  const role = data.roles.find((x) => x.role_key === roleKey);

  return (
    <div className="border border-slate-200 bg-white mb-8" data-testid="kpi-editor">
      <div className="p-5 border-b border-slate-200 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Settings2 size={11} strokeWidth={1.5} /> KPI-Editor · nur Admin/Manager/GF</div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">KPIs pro Rolle festlegen — nur messbare Quellen</div>
        </div>
        <select className="input-tech text-xs" value={roleKey} onChange={(e) => selectRole(e.target.value)} data-testid="kpi-editor-role">
          {data.roles.map((r) => <option key={r.role_key} value={r.role_key}>{r.name}{r.is_custom ? " · angepasst" : ""}</option>)}
        </select>
      </div>
      <div className="p-5 space-y-2">
        {kpis.map((k, i) => (
          <div key={k.id || i} className="grid grid-cols-1 lg:grid-cols-[2fr_90px_80px_120px_2fr_auto] gap-2 items-center border border-slate-100 p-2" data-testid={`kpi-editor-row-${i}`}>
            <input className="input-tech text-xs" value={k.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="KPI-Name" data-testid={`kpi-editor-name-${i}`} />
            <input type="number" min="0.5" step="0.5" className="input-tech text-xs font-mono" value={k.target} onChange={(e) => upd(i, "target", e.target.value)} data-testid={`kpi-editor-target-${i}`} />
            <input className="input-tech text-xs" value={k.unit} onChange={(e) => upd(i, "unit", e.target.value)} placeholder="Stk" data-testid={`kpi-editor-unit-${i}`} />
            {isCountable(k.unit) ? (
              <div className="text-[10px] text-slate-500 leading-tight" title="Stk/Anzahl/UE-Ziele werden automatisch pro Woche runtergerechnet (Monatsziel ÷ 4)" data-testid={`kpi-editor-period-auto-${i}`}>
                pro Monat<br /><span className="text-slate-400">Woche = ÷4 automatisch</span>
              </div>
            ) : (
              <select className="input-tech text-xs" value={k.period} onChange={(e) => upd(i, "period", e.target.value)} data-testid={`kpi-editor-period-${i}`}>
                <option value="monthly">pro Monat</option>
                <option value="weekly">pro Woche</option>
              </select>
            )}
            <select className="input-tech text-xs" value={k.source} onChange={(e) => upd(i, "source", e.target.value)} data-testid={`kpi-editor-source-${i}`}>
              {data.sources.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              {!data.sources.some((s) => s.id === k.source) && <option value={k.source}>{k.source}</option>}
            </select>
            <button onClick={() => setKpis((arr) => arr.filter((_, idx) => idx !== i))}
              className="text-slate-400 hover:text-red-600 justify-self-center" data-testid={`kpi-editor-del-${i}`}>
              <Trash2 size={13} strokeWidth={1.5} />
            </button>
          </div>
        ))}
        {kpis.length === 0 && <div className="text-sm text-slate-400 italic text-center py-4">Keine KPIs für diese Rolle.</div>}
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <button onClick={() => setKpis((arr) => [...arr, { name: "", target: 10, unit: "Stk", period: "monthly", source: "manual" }])}
            className="text-xs px-3 py-2 border border-slate-300 text-slate-700 hover:border-slate-900 flex items-center gap-1.5" data-testid="kpi-editor-add">
            <Plus size={11} strokeWidth={2} /> KPI hinzufügen
          </button>
          <div className="flex-1" />
          {role?.is_custom && (
            <button onClick={reset} className="text-xs px-3 py-2 border border-slate-300 text-slate-600 hover:border-red-600 hover:text-red-600 flex items-center gap-1.5" data-testid="kpi-editor-reset">
              <RotateCcw size={11} strokeWidth={1.5} /> Auf Standard zurücksetzen
            </button>
          )}
          <button onClick={save} disabled={saving}
            className="text-xs px-4 py-2 bg-slate-900 text-white hover:bg-black flex items-center gap-1.5 disabled:opacity-50" data-testid="kpi-editor-save">
            <Save size={11} strokeWidth={1.5} /> {saving ? "Speichert…" : "KPIs speichern"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KpiEditor;
